import React, { useState } from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  CircularProgress,
} from '@mui/material';
import { useQuery } from 'react-query';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { editeursAPI } from '../../services/api';

// Fonction de normalisation pour la comparaison (insensible aux accents)
const normalizeString = (str) => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

function EditeurSelector({ control, name, disabled = false, label = "Éditeur" }) {
  const { t } = useTranslation();
  const [editeurSearchTerm, setEditeurSearchTerm] = useState('');

  // Recherche d'éditeurs
  const { data: editeursData, isLoading: editeursLoading } = useQuery(
    ['editeurs-list', editeurSearchTerm],
    () => editeursAPI.search(editeurSearchTerm).then(res => res.data.data),
    {
      enabled: editeurSearchTerm.length >= 2,
      staleTime: 2 * 60 * 1000,
      cacheTime: 5 * 60 * 1000
    }
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
          <Autocomplete
            {...field}
            multiple
            freeSolo
            options={editeursData || []}
            getOptionLabel={(option) => {
              // Pour les options d'ajout
              if (option.isAddOption) {
                return t('editeurSelector.addOption', { value: option.inputValue });
              }
              // Pour les nouvelles valeurs string
              if (typeof option === 'string') return option;
              // Pour les objets avec displayName
              if (option.displayName) return option.displayName;
              // Pour les éditeurs existants
              return option.nom || '';
            }}
            isOptionEqualToValue={(option, value) => {
              if (typeof option === 'object' && typeof value === 'object') {
                if (option.id && value.id) return option.id === value.id;
                if (option.isNew && value.isNew) return option.displayName === value.displayName;
              }
              return false;
            }}
            filterOptions={(options, params) => {
              const normalizedInput = normalizeString(params.inputValue);
              
              const filtered = options.filter(option => {
                return normalizeString(option.nom).includes(normalizedInput);
              });

              const { inputValue } = params;
              // Suggérer l'ajout d'un nouvel éditeur si la saisie ne correspond à aucun existant
              const isExisting = options.some(option => 
                normalizeString(option.nom) === normalizedInput
              );
              
              if (inputValue !== '' && !isExisting) {
                filtered.push({
                  inputValue,
                  nom: inputValue,
                  displayName: inputValue,
                  isNew: true,
                  isAddOption: true
                });
              }

              return filtered;
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const tagProps = getTagProps({ index });
                const { key, ...otherTagProps } = tagProps;
                
                return (
                  <Chip
                    key={option.id || option.displayName || index}
                    variant={option.isNew ? "filled" : "outlined"}
                    color={option.isNew ? "secondary" : "default"}
                    label={
                      typeof option === 'string' ? option :
                      option.displayName ? 
                        (option.isNew ? `${option.displayName} (${t('editeurSelector.new')})` : option.displayName) :
                      option.nom || ''
                    }
                    {...otherTagProps}
                    onClick={(e) => {
                      // Empêcher la suppression du chip lors du clic
                      e.stopPropagation();
                      
                      // Obtenir le texte à éditer
                      const textToEdit = typeof option === 'string' ? option :
                        option.displayName || option.nom || '';
                      
                      // Mettre le texte dans le champ de recherche
                      setEditeurSearchTerm(textToEdit);
                      
                      // Vider le champ (l'éditeur n'est pas multiple, donc on le supprime)
                      field.onChange(null);
                      
                      console.log('✏️ Édition du chip d\'éditeur:', textToEdit);
                    }}
                    onDelete={tagProps.onDelete}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: option.isNew ? 'secondary.light' : 'action.hover',
                      }
                    }}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                placeholder={editeursLoading ? t('editeurSelector.loading') : t('editeurSelector.placeholder')}
                margin="none"
                helperText={t('editeurSelector.helperText')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {editeursLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            onChange={(event, newValue, reason, details) => {
              console.log('🔄 EditeurSelector onChange:', { newValue, reason, details });
              
              // Si on ajoute par freeSolo (entrée au clavier)
              if (reason === 'createOption' && details?.option && typeof details.option === 'string') {
                const inputValue = details.option.trim();
                
                const newEditeur = {
                  isNew: true,
                  nom: inputValue,
                  displayName: inputValue
                };
                
                // Pour l'éditeur, on ne garde qu'un seul élément (pas multiple)
                field.onChange(newEditeur);
                console.log('✅ Nouvel éditeur ajouté via freeSolo:', newEditeur);
                return;
              }
              
              // Traiter les autres cas
              if (newValue && newValue.length > 0) {
                const option = newValue[newValue.length - 1]; // Prendre le dernier élément
                
                if (typeof option === 'string') {
                  // Nouvel éditeur saisi directement
                  const processedEditeur = {
                    isNew: true,
                    nom: option.trim(),
                    displayName: option.trim()
                  };
                  field.onChange(processedEditeur);
                } else if (typeof option === 'object') {
                  if (option.id) {
                    // Éditeur existant
                    field.onChange(option);
                  } else if (option.isNew) {
                    // Nouvel éditeur via l'option "Ajouter"
                    field.onChange(option);
                  }
                }
              } else {
                field.onChange(null);
              }
              
              console.log('✅ Éditeur sélectionné:', newValue);
            }}
            onInputChange={(event, newInputValue) => {
              setEditeurSearchTerm(newInputValue);
            }}
            inputValue={editeurSearchTerm}
            disabled={disabled}
            value={field.value ? [field.value] : []} // Convertir en array pour multiple
          />
      )}
    />
  );
}

export default EditeurSelector;
